import Button from "@mui/material/Button";
import MuiLink from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { StatusChip } from "components/StatusChip";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import React from "react";
import { Link } from "react-router-dom";

import {
  expiryDisplay,
  formatDate,
  matchKindLabel,
  ReviewDecision,
  reviewDecisionState,
  ReviewRow,
  violationDateRange,
} from "./reviewDisplay";

/** Who decided the group and when — accepted (released) or rejected (dismissed). */
export function DecidedByCell({ row }: { row: ReviewRow }) {
  const decidedAt = row.acceptedAt ?? row.rejectedAt;
  if (!decidedAt) {
    return null;
  }
  const label = row.acceptedAt ? "Released" : "Rejected";
  const byName = (row.acceptedAt ? row.acceptedByName : row.rejectedByName) ?? "Encamp";
  return (
    <Stack>
      <Typography variant="body2">
        {label} · {byName}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {formatDate(decidedAt)}
      </Typography>
    </Stack>
  );
}

/**
 * Facility over customer. A fleet-wide queue has to name the customer, but as
 * its own column it repeated one value down the page while the columns that
 * actually differ per row were truncated.
 */
export function FacilityCell({ row }: { row: ReviewRow }) {
  return (
    <Stack sx={{ minWidth: 0 }}>
      <MuiLink
        component={Link}
        to={`/o/${row.tenantId}/facilities/${row.facilityId}/profile`}
        target="_blank"
        variant="body2"
        onClick={(event) => event.stopPropagation()}
      >
        {row.facilityName}
      </MuiLink>
      <Typography variant="caption" color="text.secondary">
        {row.tenantName}
      </Typography>
    </Stack>
  );
}

/**
 * The matched value, what kind of match it was, and — when it applies — that the
 * same ID is still live at another site.
 *
 * "Live elsewhere" sits here rather than in its own column because it is a fact
 * *about this ID*, and as a column it was blank for almost every row while
 * costing width the truncated columns needed.
 */
export function MatchedIdCell({ row }: { row: ReviewRow }) {
  return (
    <Stack sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2">{row.altIdValue}</Typography>
        {row.activeElsewhere ? <StatusChip color="warning" label="Live elsewhere" /> : null}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {matchKindLabel(row.matchKind)}
      </Typography>
    </Stack>
  );
}

/**
 * The expiry behind the group, or an orphaned marker when the ID row was deleted
 * outright rather than expired — a state, so it reads as a chip rather than as
 * text pretending to be a date.
 */
export function ExpiredCell({ row }: { row: ReviewRow }) {
  if (row.alternateIds.length === 0) {
    return <StatusChip color="default" label="Orphaned — ID deleted" />;
  }
  return <Typography variant="body2">{expiryDisplay(row.alternateIds)}</Typography>;
}

/**
 * Three-way decision from the row: pending offers Accept + Reject, a decided row
 * only its reversal. Actions carry the staff colour per the design system —
 * contained blue would read as a customer-facing primary.
 */
export function DecisionCell({
  row,
  decide,
  saving,
}: {
  row: ReviewRow;
  decide: (row: ReviewRow, decision: ReviewDecision) => void;
  saving: boolean;
}) {
  const state = reviewDecisionState(row);
  // The row opens the drawer; deciding from the row must not also open it.
  const act = (decision: ReviewDecision) => (event: React.MouseEvent) => {
    event.stopPropagation();
    decide(row, decision);
  };
  if (state === "accepted") {
    return (
      <Button size="small" variant="text" color="staff" disabled={saving} onClick={act("unaccept")}>
        Un-accept
      </Button>
    );
  }
  if (state === "rejected") {
    return (
      <Button size="small" variant="text" color="staff" disabled={saving} onClick={act("unreject")}>
        Un-reject
      </Button>
    );
  }
  return (
    <Stack direction="row" spacing={1}>
      <Button
        size="small"
        variant="contained"
        color="staff"
        disabled={saving}
        onClick={act("accept")}
      >
        Accept
      </Button>
      <Button size="small" variant="text" color="staff" disabled={saving} onClick={act("reject")}>
        Reject
      </Button>
    </Stack>
  );
}

/**
 * Only the columns whose values the database can order by are sortable — the
 * rest are derived per row after the page is fetched.
 */
export function buildViolationMatchColumns(
  decide: (row: ReviewRow, decision: ReviewDecision) => void,
  saving: boolean
): OmnisearchGridColDef<ReviewRow>[] {
  return [
    {
      field: "facilityName",
      headerName: "Facility",
      flex: 0.9,
      minWidth: 170,
      renderCell: ({ row }) => <FacilityCell row={row} />,
    },
    {
      field: "altIdValue",
      headerName: "Matched ID",
      flex: 0.8,
      minWidth: 170,
      sortable: false,
      renderCell: ({ row }) => <MatchedIdCell row={row} />,
    },
    {
      field: "alternateIds",
      headerName: "Expired",
      flex: 0.5,
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => <ExpiredCell row={row} />,
    },
    {
      field: "violationCount",
      headerName: "Held",
      flex: 0.3,
      minWidth: 70,
      sortable: false,
    },
    {
      field: "violationDates",
      headerName: "Violation dates",
      flex: 1.2,
      minWidth: 150,
      sortable: false,
      valueGetter: (_, row) => violationDateRange(row),
    },
    {
      field: "firstSeenAt",
      headerName: "First seen",
      flex: 0.4,
      minWidth: 115,
      valueGetter: (_, row) => formatDate(row.firstSeenAt),
    },
    {
      // Empty in the default view; naming who decided is why decided rows are
      // worth showing at all.
      field: "acceptedAt",
      headerName: "Decided by",
      flex: 0.6,
      minWidth: 150,
      // Not sortable: the field only orders by acceptedAt, so a sort would file
      // every rejection under "no date".
      sortable: false,
      renderCell: ({ row }) => <DecidedByCell row={row} />,
    },
    {
      field: "decision",
      headerName: "",
      // Fixed, not flex: a flexed action column shrinks under width pressure
      // until a button is clipped mid-word. Wide enough for the pending pair
      // (Accept + Reject).
      width: 190,
      sortable: false,
      align: "right",
      renderCell: ({ row }) => <DecisionCell row={row} decide={decide} saving={saving} />,
    },
  ];
}

/**
 * Every column field, so the omnisearch filter popover can be suppressed
 * wholesale: the resolver treats `search` as plain free text, so any `key:value`
 * token the popover produced would match nothing and read as an empty backlog.
 */
export const VIOLATION_MATCH_COLUMN_FIELDS = [
  "facilityName",
  "altIdValue",
  "alternateIds",
  "violationCount",
  "violationDates",
  "firstSeenAt",
  "acceptedAt",
  "decision",
];
